import type { CancelablePromise } from "@/client/core/CancelablePromise"
import { OpenAPI } from "@/client/core/OpenAPI"
import { request as __request } from "@/client/core/request"
// Dashboard API service - temporary until client is regenerated
import type {
  AttemptMplResponse,
  AttemptSubjectMplResponse,
  FlashcardCardStat,
  StudentDashboardResponse,
  SubjectPercentileHistoryResponse,
  TeacherDashboardResponse,
  TeacherFlashcardDashboardResponse,
} from "@/types/dashboard"

export type GetStudentDashboardData = {
  startDate?: string | null
  endDate?: string | null
}

export type GetTeacherDashboardData = {
  startDate?: string | null
  endDate?: string | null
  programIds?: string[]
}

export type GetTeacherFlashcardDashboardData = {
  programIds?: string[]
}

/**
 * Dashboard service for student and teacher dashboards
 * TODO: Replace with auto-generated service when client is regenerated
 */
export const DashboardService = {
  /**
   * Get Student Dashboard
   * Returns student dashboard data with performance metrics
   */
  getStudentDashboard(
    data: GetStudentDashboardData = {},
  ): CancelablePromise<StudentDashboardResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dashboard/student",
      query: {
        start_date: data.startDate,
        end_date: data.endDate,
      },
      errors: {
        422: "Validation Error",
      },
    })
  },

  getSubjectPercentileHistory(data: {
    subjectId: string
  }): CancelablePromise<SubjectPercentileHistoryResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dashboard/subject-percentile-history",
      query: {
        subject_id: data.subjectId,
      },
      errors: {
        404: "Not Found",
        422: "Validation Error",
      },
    })
  },

  updateAttemptMpl(data: {
    attemptId: string
    minimumPassingLevel: number
  }): CancelablePromise<AttemptMplResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/dashboard/student/attempts/{attempt_id}/mpl",
      path: {
        attempt_id: data.attemptId,
      },
      body: {
        minimum_passing_level: data.minimumPassingLevel,
      },
      mediaType: "application/json",
      errors: {
        404: "Not Found",
        422: "Validation Error",
      },
    })
  },

  updateAttemptSubjectMpl(data: {
    attemptId: string
    subjectMplValues: Record<string, number>
  }): CancelablePromise<AttemptSubjectMplResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/api/v1/dashboard/student/attempts/{attempt_id}/subject-mpl",
      path: {
        attempt_id: data.attemptId,
      },
      body: {
        subject_mpl_values: data.subjectMplValues,
      },
      mediaType: "application/json",
      errors: {
        404: "Not Found",
        422: "Validation Error",
      },
    })
  },

  /**
   * Get Teacher Dashboard
   * Returns teacher dashboard data with org-wide analytics
   */
  getTeacherDashboard(
    data: GetTeacherDashboardData = {},
  ): CancelablePromise<TeacherDashboardResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dashboard/teacher",
      query: {
        start_date: data.startDate,
        end_date: data.endDate,
        program_ids: data.programIds,
      },
      errors: {
        403: "Forbidden",
        422: "Validation Error",
      },
    })
  },

  /**
   * Get teacher-dashboard flashcard analytics.
   */
  getTeacherFlashcardDashboard(
    data: GetTeacherFlashcardDashboardData = {},
  ): CancelablePromise<TeacherFlashcardDashboardResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dashboard/teacher/flashcards",
      query: {
        program_ids: data.programIds,
      },
      errors: {
        401: "Unauthorized",
        403: "Forbidden",
      },
    })
  },

  /**
   * Get card-level difficulty stats for one deck (last 180 days).
   * Lazy — only called when teacher expands a deck row.
   */
  getFlashcardDeckCards(
    deckId: string,
  ): CancelablePromise<FlashcardCardStat[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dashboard/teacher/flashcards/decks/{deck_id}/cards",
      path: {
        deck_id: deckId,
      },
      errors: {
        401: "Unauthorized",
        403: "Forbidden",
        404: "Not Found",
      },
    })
  },
}
